import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { api, normalizeText, slugSegment, normalizeProduct, normalizeTaxonomy } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import useSEO from "@/hooks/useSEO";

const STATUSES = ["Tersedia", "Terjual", "Segera Hadir", "Hubungi Kami"];
const CONDITIONS = ["Baru", "Rekondisi", "Bekas"];

export default function ProductsPage() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const brand = params.get("brand") || "";
  const stock_status = params.get("stock_status") || "";
  const condition = params.get("condition") || "";
  const sort = params.get("sort") || "newest";

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  useEffect(() => {
    // Normalize responses defensively — older records may use different field names.
    api.get("/categories")
      .then((r) => setCategories((r.data || []).map((c) => normalizeTaxonomy(c, "category"))))
      .catch(() => setCategories([]));
    api.get("/brands")
      .then((r) => setBrands((r.data || []).map((b) => normalizeTaxonomy(b, "brand"))))
      .catch(() => setBrands([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = { q, category, brand, stock_status, condition, sort };
    Object.keys(p).forEach((k) => !p[k] && delete p[k]);
    api.get("/products", { params: p })
      .then((r) => setProducts((r.data || []).map(normalizeProduct)))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [q, category, brand, stock_status, condition, sort]);

  const clearAll = () => setParams({});
  const activeCount = [category, brand, stock_status, condition, q].filter(Boolean).length;

  // Dynamic SEO — category-aware so category-filter URLs behave like "category pages".
  const seoTitle = category
    ? `${category} | Teknik Diesel Indonesia`
    : brand
    ? `Produk ${brand} | Teknik Diesel Indonesia`
    : "Katalog Produk | Teknik Diesel Indonesia";
  const seoDesc = category
    ? `Katalog ${category} — jelajahi produk ${category} yang tersedia di Teknik Diesel Indonesia.`
    : brand
    ? `Katalog produk ${brand} yang tersedia di Teknik Diesel Indonesia.`
    : "Jelajahi seluruh katalog mesin & peralatan industri Teknik Diesel Indonesia: kompresor Airman, bar bender/cutter, bar roller, spare parts, dan produk industrial lainnya.";
  const seoPath = category
    ? `/produk?category=${encodeURIComponent(category)}`
    : brand
    ? `/produk?brand=${encodeURIComponent(brand)}`
    : "/produk";
  useSEO({ title: seoTitle, description: seoDesc, path: seoPath });

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <div className="mb-8">
        <div className="font-mono-tech text-xs uppercase tracking-widest text-[#F59E0B] mb-2">Katalog Produk</div>
        <h1 className="font-display font-extrabold text-white text-4xl lg:text-5xl">Katalog Mesin & Peralatan</h1>
        <p className="text-slate-400 mt-3 max-w-3xl">Jelajahi seluruh produk Teknik Diesel Indonesia termasuk kompresor Airman, bar bender/cutter Takeda dan Toyo, serta produk industrial lainnya.</p>
      </div>

      <div className="flex items-center gap-3 mb-5">
        <div className="flex items-center bg-[#161E2E] border border-[#334155] flex-1 max-w-md">
          <Search size={16} className="text-slate-500 ml-3" />
          <input
            value={q}
            onChange={(e) => update("q", e.target.value)}
            placeholder="Cari nama, model, brand..."
            className="bg-transparent px-3 py-2.5 text-sm w-full outline-none text-slate-100 placeholder:text-slate-500"
            data-testid="products-search-input"
          />
        </div>
        <select value={sort} onChange={(e) => update("sort", e.target.value)} className="bg-[#161E2E] border border-[#334155] text-slate-200 text-sm px-3 py-2.5 outline-none focus:border-[#F59E0B]" data-testid="products-sort-select">
          <option value="newest">Produk Terbaru</option>
          <option value="name_asc">Nama A–Z</option>
          <option value="name_desc">Nama Z–A</option>
        </select>
        <button onClick={() => setShowFilters(!showFilters)} className="lg:hidden bg-[#161E2E] border border-[#334155] text-slate-200 px-3 py-2.5 flex items-center gap-2 text-sm" data-testid="toggle-filters">
          <SlidersHorizontal size={14} /> Filter {activeCount > 0 && <span className="bg-[#F59E0B] text-[#0B0F17] text-[10px] px-1.5 py-0.5">{activeCount}</span>}
        </button>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className={`${showFilters ? "block" : "hidden lg:block"} space-y-6`}>
          <FilterGroup title="Kategori" testid="filter-category">
            <FilterOption label="Semua" active={!category} onClick={() => update("category", "")} testid="filter-category-all" />
            {categories.filter((c) => c.name).map((c) => (
              <FilterOption
                key={c.id || c.slug || c.name}
                label={`${c.name} (${c.count})`}
                active={category === c.name}
                onClick={() => update("category", c.name)}
                testid={`filter-category-${slugSegment(c.slug || c.name)}`}
              />
            ))}
          </FilterGroup>
          <FilterGroup title="Brand" testid="filter-brand">
            <FilterOption label="Semua" active={!brand} onClick={() => update("brand", "")} testid="filter-brand-all" />
            {brands.filter((b) => b.name).map((b) => (
              <FilterOption
                key={b.id || b.slug || b.name}
                label={`${b.name} (${b.count})`}
                active={brand === b.name}
                onClick={() => update("brand", b.name)}
                testid={`filter-brand-${slugSegment(b.slug || b.name)}`}
              />
            ))}
          </FilterGroup>
          <FilterGroup title="Status Stok" testid="filter-status">
            <FilterOption label="Semua" active={!stock_status} onClick={() => update("stock_status", "")} testid="filter-status-all" />
            {STATUSES.map((s) => (
              <FilterOption key={s} label={s} active={stock_status === s} onClick={() => update("stock_status", s)} testid={`filter-status-${slugSegment(s)}`} />
            ))}
          </FilterGroup>
          <FilterGroup title="Kondisi" testid="filter-condition">
            <FilterOption label="Semua" active={!condition} onClick={() => update("condition", "")} />
            {CONDITIONS.map((c) => (
              <FilterOption key={c} label={c} active={condition === c} onClick={() => update("condition", c)} testid={`filter-condition-${slugSegment(c)}`} />
            ))}
          </FilterGroup>
          {activeCount > 0 && (
            <Button variant="outline" onClick={clearAll} className="w-full border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-[#0B0F17] rounded-sm" data-testid="clear-filters">
              Hapus Semua Filter
            </Button>
          )}
        </aside>

        <div>
          {loading ? (
            <div className="text-center py-20 text-slate-400">Memuat produk...</div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-[#334155] bg-[#161E2E]/40" data-testid="empty-products">
              <div className="text-6xl mb-4">📦</div>
              <div className="font-display font-bold text-white text-xl mb-2">Tidak ada produk ditemukan</div>
              <p className="text-slate-400 mb-4">Coba ubah filter atau kata kunci pencarian Anda.</p>
              <Button onClick={clearAll} className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B0F17] rounded-sm">Reset Filter</Button>
            </div>
          ) : (
            <>
              <div className="text-sm text-slate-400 mb-4 font-mono-tech">Menampilkan {products.length} produk</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {products.map((p) => <ProductCard key={p.id || p.slug || normalizeText(p.product_name)} product={p} />)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ title, children, testid }) {
  return (
    <div data-testid={testid}>
      <h4 className="font-mono-tech text-[10px] uppercase tracking-widest text-[#F59E0B] mb-3 pb-2 border-b border-[#334155]">{title}</h4>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function FilterOption({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className={`block w-full text-left text-sm py-1.5 px-2 transition ${active ? "bg-[#F59E0B]/10 text-[#F59E0B] border-l-2 border-[#F59E0B]" : "text-slate-300 hover:text-[#F59E0B] border-l-2 border-transparent"}`}
      data-testid={testid}
    >
      {label}
    </button>
  );
}
